import { useEffect, useState } from 'react';
import { Input, Button } from '@playcastdotio/Shadcn';
import { Form, useNavigate } from 'react-router-dom';
import { faker } from '@faker-js/faker';
import { invitationState, playerNameState } from '../../../api/stores/lobbyStores';
import { joinState } from '../../../api/stores/globalStores';
import { saveDisplayName } from '../../../api/modules/player';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';

export const Invitation = () => {
  const [isValid, setIsValid] = useState(true);
  const [canJoin, setCanJoin] = useState(false);
  const [inviteData, setInviteData] = useState(invitationState.currentState.selectedInvite);
  const [defaultAlias, setDefaultAlias] = useState<string | undefined>(invitationState.currentState.defaultAlias);
  const [defaultAliasPlatform, setDefaultAliasPlatform] = useState<IdentityPlatform | undefined>(
    invitationState.currentState.defaultAliasPlatform,
  );

  useEffect(() => {
    // Initial state check
    const checkInviteValidity = (invite = invitationState.currentState.selectedInvite) => {
      if (!invite) {
        setIsValid(false);
        setCanJoin(false);
        return;
      }
      if (invite.invalid) {
        setIsValid(false);
        setCanJoin(false);
        return;
      }

      setIsValid(true);
      setInviteData(invite);

      if (invite.type === 'lobby') {
        setCanJoin(invite.manifest.lobbyOptions.canJoin);
      }
    };

    // Check initial state
    checkInviteValidity();

    // Set up listener for state changes
    const listener = invitationState.addListener((state) => {
      checkInviteValidity(state.selectedInvite);

      // Check for defaultAlias and defaultAliasPlatform in the response
      if (state.selectedInvite === undefined && state.defaultAlias && state.defaultAliasPlatform) {
        setDefaultAlias(state.defaultAlias);
        setDefaultAliasPlatform(state.defaultAliasPlatform);
      }
    });

    return () => {
      invitationState.removeListener(listener);
    };
  }, []); // Empty dependency array since we're using currentState inside

  return isValid ? (
    <ValidLink canJoin={canJoin} inviteData={inviteData} />
  ) : (
    <InvalidLink defaultAlias={defaultAlias} defaultAliasPlatform={defaultAliasPlatform} />
  );
};

const InvalidLink = ({ defaultAlias, defaultAliasPlatform }: { defaultAlias?: string; defaultAliasPlatform?: IdentityPlatform }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // If we have both defaultAlias and defaultAliasPlatform, redirect to the appropriate URL
    console.log('defaultAlias', defaultAlias);
    console.log('defaultAliasPlatform', defaultAliasPlatform);
    if (defaultAlias && defaultAliasPlatform) {
      let platformPrefix = 'p'; // Default to Clerk

      // Determine the platform prefix based on the platform
      if (defaultAliasPlatform === IdentityPlatform.DISCORD) {
        platformPrefix = 'pd';
      } else if (defaultAliasPlatform === IdentityPlatform.TWITCH) {
        platformPrefix = 'pt';
      }

      // Construct the redirect URL
      const redirectUrl = `/${platformPrefix}/${defaultAlias}`;

      // Redirect to the new URL
      navigate(redirectUrl);
    }
  }, [defaultAlias, defaultAliasPlatform, navigate]);

  return (
    <div></div>
    // <div className='mt-8 flex w-full flex-col items-center justify-center gap-4'>
    //   <div className='bg-footer w-full space-y-4 rounded p-4 py-6 text-center shadow-md md:w-2/3 lg:w-2/5'>
    //     <h3 className='row-auto text-xl font-bold'>Confirm Host Status or Get New Link</h3>
    //     <p className='row-auto'>
    //       Your host link may have expired or the host has not started the session. Get a new link or confirm the host has started and
    //       refresh the page.
    //     </p>
    //   </div>
    //   <div>
    //     <Link to='https://playcast.io' className={clsx(buttonVariants(), 'max-w-48 text-wrap py-8 text-center')}>
    //       Host Your Own Session on Playcast.io
    //     </Link>
    //   </div>
    // </div>
  );
};

const ValidLink = ({ canJoin, inviteData }: { canJoin: boolean; inviteData: typeof invitationState.currentState.selectedInvite }) => {
  const [name, setName] = useState('');
  const [placeholderName] = useState(() => faker.person.firstName() + ' ' + faker.person.lastName());
  const [description, setDescription] = useState(inviteData?.description || '');

  useEffect(() => {
    // Initialize name from state or generate placeholder
    const storedName = playerNameState.currentState.name;
    console.log('Invitation: Initializing name from state:', storedName);
    setName(storedName || placeholderName);

    // Listen for player name changes
    const nameListener = playerNameState.addListener((state) => {
      console.log('Invitation: playerNameState listener triggered with:', state.name);
      if (state.name && state.name !== name) {
        setName(state.name);
      }
    });

    // Listen for invitation changes
    const inviteListener = invitationState.addListener((state) => {
      if (state.selectedInvite?.description !== description) {
        setDescription(state.selectedInvite?.description || '');
      }
    });

    return () => {
      playerNameState.removeListener(nameListener);
      invitationState.removeListener(inviteListener);
    };
  }, []); // Empty dependency array since we handle updates via listeners

  useEffect(() => {
    if (inviteData?.description !== description) {
      setDescription(inviteData?.description || '');
    }
  }, [inviteData]);

  const handleJoin = (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    const finalName = name || placeholderName;
    console.log('Invitation: handleJoin called with finalName:', finalName);
    console.log('Invitation: name from state:', name);
    console.log('Invitation: placeholderName:', placeholderName);
    playerNameState.updateState({ name: finalName });

    if (name) {
      saveDisplayName();
    }

    joinState.updateState({
      ...joinState.currentState,
      state: 'inQueue',
    });
  };

  return (
    <div className='flex max-w-sm flex-col items-center justify-center gap-4 justify-self-center text-center lg:gap-8'>
      <p className='row-auto mx-auto my-0 text-lg italic'>Play Together Anywhere, Anytime</p>
      <h2 className='row-auto -mt-4 text-2xl font-bold lg:text-3xl' data-testid='stream-title'>
        {description}
      </h2>
      <div className='row-auto w-full gap-2'>
        <Form onSubmit={handleJoin}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            type='text'
            id='playerName'
            name='playerName'
            required
            placeholder={placeholderName}
            className='bg-content mb-2 h-auto w-full rounded border-none px-4 py-3 text-center text-lg shadow-sm'
          />
          <Button
            type='submit'
            className='rounded-full bg-amber-400'
            data-testid='player-join-button'
            disabled={!canJoin || !name}
            style={{
              boxShadow: 'inset 0 4px 2px rgba(251, 191, 36, 0.5), inset 0 -4px 2px rgba(146, 64, 14, 0.5), 0 4px 4px rgba(0, 0, 0, 0.4)',
              WebkitBoxShadow: 'inset 0 4px 2px rgba(251, 191, 36, 0.5), inset 0 -4px 2px rgba(146, 64, 14, 0.5)',
              MozBoxShadow: 'inset 0 4px 2px rgba(251, 191, 36, 0.5), inset 0 -4px 2px rgba(146, 64, 14, 0.5)',
            }}
          >
            {canJoin ? 'Join Now' : 'Lobby Closed'}
          </Button>
        </Form>
      </div>
    </div>
  );
};

